/**
 * Legacy Bridge Utilities
 *
 * This module provides compatibility functions that expose React components
 * to legacy JavaScript code via the window.CFN namespace.
 *
 * This allows gradual migration where legacy code can use modern React dialogs
 * without a full rewrite.
 */
let dialogAPI = null;
/**
 * Register the React dialog API for use by legacy code
 * Called from the DialogProvider component
 */
export function registerDialogAPI(api) {
    dialogAPI = api;
}
/**
 * Legacy-compatible areYouSure function
 * Matches the signature of the old jQuery/Bootstrap version
 *
 * @param id_prefix - Legacy ID prefix (ignored in React version)
 * @param question - The question to ask
 * @param yesText - Text for confirm button
 * @param noText - Text for cancel button
 * @param cbYes - Callback if user confirms (receives null instead of popup)
 * @param cbNo - Callback if user cancels (receives null instead of popup)
 *
 * @example
 * CFN.areYouSure('delete', 'Delete this structure?', 'Delete', 'Cancel',
 *   () => { deleteStructure(); },
 *   () => { console.log('Cancelled'); }
 * );
 */
export async function areYouSure(id_prefix, question, yesText, noText, cbYes, cbNo) {
    if (!dialogAPI) {
        console.error('Dialog API not initialized. Legacy areYouSure cannot work.');
        return;
    }
    const confirmed = await dialogAPI.confirm({
        title: question,
        confirmText: yesText || 'Yes',
        cancelText: noText || 'Cancel',
        variant: 'destructive',
    });
    if (confirmed && cbYes) {
        cbYes(null); // Pass null instead of popup DOM element
    }
    else if (!confirmed && cbNo) {
        cbNo(null);
    }
}
/**
 * Legacy-compatible infoBox function
 * Matches the signature of the old jQuery/Bootstrap version
 *
 * @param id_prefix - Legacy ID prefix (ignored in React version)
 * @param statement - The message to display
 * @param dismissText - Text for dismiss button
 * @param cb - Callback when dismissed
 *
 * @example
 * CFN.infoBox('success', 'Structure deleted successfully', 'OK', () => {
 *   console.log('User dismissed the message');
 * });
 */
export async function infoBox(id_prefix, statement, dismissText, cb) {
    if (!dialogAPI) {
        console.error('Dialog API not initialized. Legacy infoBox cannot work.');
        return;
    }
    await dialogAPI.info({
        message: statement,
        dismissText: dismissText || 'Dismiss',
        variant: 'info',
    });
    if (cb) {
        cb();
    }
}
/**
 * Legacy-compatible quickYesCancel function
 * Simplified version of areYouSure
 *
 * @param id_prefix - Legacy ID prefix (ignored in React version)
 * @param question - The question to ask
 * @param cbYes - Callback if user confirms
 *
 * @example
 * CFN.quickYesCancel('confirm', 'Continue with this action?', () => {
 *   doAction();
 * });
 */
export async function quickYesCancel(id_prefix, question, cbYes) {
    if (!dialogAPI) {
        console.error('Dialog API not initialized. Legacy quickYesCancel cannot work.');
        return;
    }
    const confirmed = await dialogAPI.quickConfirm(question);
    if (confirmed && cbYes) {
        cbYes();
    }
}
/**
 * Initialize the legacy bridge
 * Should be called after the React app mounts
 */
export function initializeLegacyBridge(api) {
    registerDialogAPI(api);
    // Expose functions to window.CFN for use in legacy main.js code
    if (typeof window !== 'undefined') {
        window.CFN = window.CFN || {};
        // Directly expose React versions (no _react suffix needed for dev page)
        window.CFN.areYouSure = areYouSure;
        window.CFN.infoBox = infoBox;
        window.CFN.quickYesCancel = quickYesCancel;
        console.log('✅ React dialog bridge initialized');
    }
}
